import Markdown from "react-markdown";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";

import { Button } from "src/library/components/Button";
import { Icons } from "src/library/components/Icon/types";
import { getWebDomainDocument } from "src/library/utils/documents";
import { PageProps } from "src/pages/Router/routes";
import { DocumentPageType } from "src/library/utils/documents/getWebDomainDocument";

/**
 * Renders pages defined by markdown documents local in codebase
 * Path must be handled.
 *
 * @returns
 */
const DocumentPage: React.FC<PageProps<unknown>> = props => {
  const { history } = props;

  const documentType = useMemo(() => {
    const pathnameNoSlash = history.location.pathname.split("/")[ 1 ];

    switch (pathnameNoSlash) {
      case DocumentPageType.PrivacyPolicy:
        return DocumentPageType.PrivacyPolicy;

      case DocumentPageType.TermsAndConditions:
        return DocumentPageType.TermsAndConditions;

      default:
        return null;
    }
  }, [ history.location.pathname ]);

  // get document based on path
  const subDomain = window.location.host;
  const [ doc, setDoc ] = useState<string>("");

  // Sets up which document to use based on pageType
  const getDoc = useCallback(async () => {
    if (!documentType) {
      toast.error(`
        Pathname does not match a known document type. 
        Please create the document type in order to link to the relevant document.
      `);

      // return early as document type not set up.
      // Make sure to add the new document to the document type enum when setting up a new document.
      return;
    }

    const document = await getWebDomainDocument(documentType, subDomain);

    setDoc(document);
  }, [ documentType, subDomain ]);

  useEffect(() => {
    getDoc();
  }, [ getDoc ]);

  return (
    <DocumentWrapper>
      <Button
        $buttonType="primary"
        icon={Icons.CLOSE}
        onClick={() => history.goBack()}
      >
        Back
      </Button>

      <Markdown
        remarkPlugins={[ ]}
        components={{ strong: "h3" }}
        allowedElements={[
          "strong",
          "p",
          "br"
        ]}
      >
        {doc}
      </Markdown>

    </DocumentWrapper>
  );
};

const DocumentWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    margin: 20px;
    justify-content: left;

    p {
      font-size: ${theme.fonts.sizes.p};
      padding-bottom: 10px;
    }

    h3 {
      font-weight: ${theme.fonts.weights.bold};
      font-size: ${theme.fonts.sizes.h4};
      padding-top: ${theme.spacing.padding.sm};
      padding-bottom: ${theme.spacing.padding.xs};
    }
  `}
`;

export default DocumentPage;
